import { Dimensions, Platform } from "react-native";

/** Max list height matches dropdown styles (~200px). */
const DEFAULT_MAX_MENU_HEIGHT = 200;
const EDGE_PAD = 8;
const MIN_MENU_SCROLL_H = 48;

export function getDropdownViewportHeightPx() {
  if (typeof window !== "undefined") {
    if (Platform.OS === "web") {
      const vv = window.visualViewport;
      if (vv && typeof vv.height === "number" && vv.height > 40) return vv.height;
      if (typeof window.innerHeight === "number") return window.innerHeight;
      if (typeof document !== "undefined" && document.documentElement) {
        return document.documentElement.clientHeight || Dimensions.get("window").height;
      }
    }
  }
  return Dimensions.get("window").height;
}

/**
 * Viewport-aligned rect for positioning a portaled dropdown menu on web.
 * Uses sync DOM reads so nested scroll containers (overflow:auto) stay aligned between frames.
 */
export function readMenuAnchorRectSync(node) {
  if (!node) return null;
  if (Platform.OS === "web" && typeof node.getBoundingClientRect === "function") {
    try {
      const r = node.getBoundingClientRect();
      return {
        x: r.left,
        y: r.top,
        width: r.width > 1 ? r.width : 200,
        height: r.height > 1 ? r.height : 60,
      };
    } catch (_e) {
      return null;
    }
  }
  return null;
}

/**
 * @returns {{ menuTop: number, menuMaxHeight: number }}
 */
export function computeDropdownMenuPlacement(menuRect, viewportHeight) {
  if (!menuRect) {
    return { menuTop: 0, menuMaxHeight: DEFAULT_MAX_MENU_HEIGHT };
  }
  const vh =
    viewportHeight > 40 ? viewportHeight : getDropdownViewportHeightPx();

  const triggerBottom = menuRect.y + menuRect.height;
  const spaceBelow = vh - triggerBottom - EDGE_PAD;

  // Default: dropdown top flush under trigger (preferred).
  let menuTop = triggerBottom;
  let menuMaxHeight = Math.min(
    DEFAULT_MAX_MENU_HEIGHT,
    Math.max(MIN_MENU_SCROLL_H, Math.floor(spaceBelow))
  );

  return { menuTop, menuMaxHeight };
}

export function rectsClose(a, b, eps = 0.75) {
  if (!a || !b) return false;
  return (
    Math.abs(a.x - b.x) < eps &&
    Math.abs(a.y - b.y) < eps &&
    Math.abs(a.width - b.width) < eps &&
    Math.abs(a.height - b.height) < eps
  );
}

function isElementScrollable(el) {
  if (!el || el.nodeType !== 1 || typeof window === "undefined") return false;
  const st = window.getComputedStyle(el);
  const oy = st.overflowY;
  const ox = st.overflowX;
  const canY =
    /(auto|scroll|overlay)/.test(oy) && el.scrollHeight > el.clientHeight + 2;
  const canX =
    /(auto|scroll|overlay)/.test(ox) && el.scrollWidth > el.clientWidth + 2;
  return canY || canX;
}

/**
 * Overscroll / scroll-to-limit often produces no extra `scroll` events while visuals still move,
 * especially with nested overflow + touch rubber-banding.
 * Guards: wheel/touchmove, ancestors' scroll (+ scrollend), visualViewport.
 * @returns {() => void} cleanup
 */
export function subscribeDropdownAnchorGuards(getAnchorNode, onReposition) {
  if (
    Platform.OS !== "web" ||
    typeof window === "undefined" ||
    typeof document === "undefined"
  ) {
    return () => {};
  }

  let raf1 = null;
  let chainedRafOuter = null;
  let chainedRafInner = null;

  const scheduleFrame = () => {
    if (raf1 != null) return;
    raf1 = window.requestAnimationFrame(() => {
      raf1 = null;
      onReposition();
    });
  };

  /** Two rAF ticks: catches scroll snap / rubber-band settling after hitting scroll extents. */
  const schedulePostScrollLayout = () => {
    if (chainedRafOuter != null) return;
    chainedRafOuter = window.requestAnimationFrame(() => {
      chainedRafInner = window.requestAnimationFrame(() => {
        chainedRafOuter = null;
        chainedRafInner = null;
        onReposition();
      });
    });
  };

  const scrollRoots = new Set();
  try {
    const node = typeof getAnchorNode === "function" ? getAnchorNode() : null;
    if (node && node.parentElement) {
      let p = node.parentElement;
      while (p) {
        if (isElementScrollable(p)) scrollRoots.add(p);
        p = p.parentElement;
      }
    }
    if (typeof document !== "undefined") {
      if (document.scrollingElement) scrollRoots.add(document.scrollingElement);
      if (document.documentElement) scrollRoots.add(document.documentElement);
      if (document.body) scrollRoots.add(document.body);
    }
  } catch (_e) {
    /* ignore */
  }

  const supportsScrollEnd = typeof window !== "undefined" && "onscrollend" in window;

  scrollRoots.forEach((el) => {
    el.addEventListener("scroll", schedulePostScrollLayout, { passive: true, capture: true });
    if (supportsScrollEnd) {
      el.addEventListener("scrollend", schedulePostScrollLayout, { passive: true });
    }
  });

  window.addEventListener("scroll", schedulePostScrollLayout, { passive: true, capture: true });
  window.addEventListener("resize", schedulePostScrollLayout, { passive: true });
  window.addEventListener("wheel", scheduleFrame, { passive: true, capture: true });
  window.addEventListener("touchmove", scheduleFrame, { passive: true, capture: true });
  window.addEventListener("touchend", schedulePostScrollLayout, { passive: true, capture: true });

  const vv = window.visualViewport;
  if (vv) {
    vv.addEventListener("scroll", schedulePostScrollLayout, { passive: true });
    vv.addEventListener("resize", schedulePostScrollLayout, { passive: true });
  }

  if (supportsScrollEnd) {
    document.addEventListener("scrollend", schedulePostScrollLayout, { passive: true, capture: true });
  }

  return () => {
    if (raf1 != null) window.cancelAnimationFrame(raf1);
    if (chainedRafOuter != null) window.cancelAnimationFrame(chainedRafOuter);
    if (chainedRafInner != null) window.cancelAnimationFrame(chainedRafInner);
    raf1 = chainedRafOuter = chainedRafInner = null;

    scrollRoots.forEach((el) => {
      el.removeEventListener("scroll", schedulePostScrollLayout, { capture: true });
      if (supportsScrollEnd) {
        el.removeEventListener("scrollend", schedulePostScrollLayout);
      }
    });
    window.removeEventListener("scroll", schedulePostScrollLayout, { capture: true });
    window.removeEventListener("resize", schedulePostScrollLayout);
    window.removeEventListener("wheel", scheduleFrame, { capture: true });
    window.removeEventListener("touchmove", scheduleFrame, { capture: true });
    window.removeEventListener("touchend", schedulePostScrollLayout, { capture: true });
    if (vv) {
      vv.removeEventListener("scroll", schedulePostScrollLayout);
      vv.removeEventListener("resize", schedulePostScrollLayout);
    }
    if (supportsScrollEnd) {
      document.removeEventListener("scrollend", schedulePostScrollLayout, { capture: true });
    }
  };
}
